import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { FadeUp, useGallery } from '../hooks';
import ContactForm from '../components/ContactForm';
import '../styles.css';

const faqsByType = {
  'oak floor': [
    { q: "How long does a hardwood floor installation take?", a: "Most residential oak floor projects run 3–7 days depending on square footage, subfloor condition, and finish type. We factor in acclimation time for the wood before installation begins." },
    { q: "Can existing hardwood floors be refinished instead of replaced?", a: "In most cases, yes — if the boards have enough thickness remaining. We assess wear, gouges, and moisture damage before recommending refinish vs. replacement." },
    { q: "What causes hardwood floors to warp or cup?", a: "Moisture imbalance between the top and bottom of the board is the primary cause. This can come from subfloor leaks, humidity swings, or improper installation." },
    { q: "What finish options are available for oak floors?", a: "We work with oil-based polyurethane, water-based poly, hardwax oil, and site-finished custom stains. Each has different durability, sheen, and maintenance profiles." },
    { q: "Is engineered oak a good alternative to solid hardwood?", a: "For high-humidity rooms or over radiant heat, engineered oak is often the better long-term choice. We'll walk you through the tradeoffs for your specific situation." },
    { q: "How do I maintain hardwood floors after installation?", a: "Dry mopping, prompt spill cleanup, and avoiding steam mops. We provide a care sheet specific to your finish at project completion." },
  ],
  'kitchen': [
    { q: "What's included in a full kitchen renovation?", a: "Cabinets, countertops, flooring, tile, plumbing, electrical, appliance fit-out, and finish trim. We coordinate all trades or work alongside your existing subs." },
    { q: "How long does a kitchen remodel take?", a: "A standard full remodel runs 4–8 weeks. Lead times on custom cabinetry and specialty materials are the most common variable." },
    { q: "Can we keep the kitchen partially functional during renovation?", a: "Often yes. We sequence demolition and rough work to minimize downtime and preserve sink or appliance access where possible." },
    { q: "What are common structural surprises in kitchen renovations?", a: "Load-bearing walls where clients want open plans, out-of-level floors, and outdated electrical that needs upsizing for modern appliances." },
    { q: "Do you handle custom cabinetry?", a: "Yes. We build and install custom cabinetry in-house and work with select local shops for specialty finishes." },
    { q: "How should I budget for a kitchen renovation?", a: "Ranges vary widely by scope and finishes. We provide detailed line-item estimates after a site visit — we don't quote from floor plans alone." },
  ],
  'home office': [
    { q: "What makes a home office conversion different from a standard renovation?", a: "Electrical load, networking infrastructure, acoustic treatment, and purpose-built storage are the key differentiators. We design for how you actually work." },
    { q: "Can you add a dedicated circuit for office equipment?", a: "Yes. We run dedicated 20A circuits for workstations, separate lines for AV, and coordinate with low-voltage contractors for structured cabling." },
    { q: "How do you handle soundproofing?", a: "Decoupled wall assemblies, resilient channel, acoustic insulation, and solid-core doors. The right solution depends on the noise source and budget." },
    { q: "Can a garage be converted into a functional office?", a: "Yes — a common project for us. Garages require attention to insulation, vapor barrier, and conditioning." },
    { q: "Do you build custom desks and built-in shelving?", a: "Yes. Built-in desks, shelving systems, and cabinetry are part of our functional art work. We design around your workflow." },
    { q: "What's the return on investment for a home office addition?", a: "A well-executed office addition typically adds value proportional to the square footage and finish quality." },
  ],
  'deck': [
    { q: "When should I rebuild instead of repair a deck?", a: "When more than 30–40% of the structure needs replacement, or when the ledger attachment or footings are compromised." },
    { q: "What decking materials do you recommend?", a: "Ipe and hardwoods for longevity; composite for low maintenance; pressure-treated for budget builds with proper detailing." },
    { q: "Do deck rebuilds require permits?", a: "In most Bay Area and Florida jurisdictions, yes — especially when changing size, height, or structural members. We handle permit documentation." },
    { q: "How do you handle the ledger-to-house connection?", a: "This is the most critical detail. We use code-compliant flashing, appropriate fasteners, and inspect the rim joist before any attachment." },
    { q: "How long does a deck rebuild take?", a: "Most residential decks run 1–3 weeks. Permit approval is the main variable. We don't start until approvals are in hand." },
    { q: "What maintenance does a new deck require?", a: "Hardwood decks need annual oiling. Composite needs periodic cleaning but no sealing. All decks benefit from keeping debris and standing water off the surface." },
  ],
};

function getFaqs(type) {
  const key = (type || '').toLowerCase();
  for (const [k, v] of Object.entries(faqsByType)) {
    if (key.includes(k)) return v;
  }
  return [
    { q: "What does this project typically involve?", a: "Every project starts with a thorough assessment of existing conditions before any work begins." },
    { q: "How long will it take?", a: "Timeline depends on scope, permit requirements, and material lead times. We provide a detailed schedule before work starts." },
    { q: "Do you handle permits?", a: "Yes. We prepare and file permit documentation as part of our standard process." },
    { q: "Will there be a dedicated crew on site?", a: "Yes. You'll have a consistent crew and a named point of contact throughout." },
    { q: "How do I get started?", a: "Fill out the contact form below. We'll schedule a site visit and follow up with a detailed estimate." },
  ];
}

function imgSrc(img) {
  return `${process.env.PUBLIC_URL}${img.src}`;
}

function imgSrcSet(img) {
  if (!img?.srcset) return undefined;
  return img.srcset.split(', ')
    .map(part => {
      const i = part.lastIndexOf(' ');
      return `${process.env.PUBLIC_URL}${part.slice(0, i)} ${part.slice(i + 1)}`;
    })
    .join(', ');
}

export default function GalleryPage() {
  const { slug } = useParams();
  const { data, loading } = useGallery(slug);
  const [openFaq, setOpenFaq] = useState(null);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    setLightbox(null);
    setOpenFaq(null);
  }, [slug]);

  // gridImages used by the keyboard lightbox effect — keep it stable before displayData
  const gridImages = data?.images?.slice(1) || [];

  useEffect(() => {
    const onKey = (e) => {
      if (lightbox === null) return;
      if (e.key === 'ArrowLeft') setLightbox(i => (i - 1 + gridImages.length) % gridImages.length);
      if (e.key === 'ArrowRight') setLightbox(i => (i + 1) % gridImages.length);
      if (e.key === 'Escape') setLightbox(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox, gridImages.length]);

  if (loading) return <div style={{ minHeight: '100vh', paddingTop: 72, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--mid)', fontFamily: 'Barlow Condensed, sans-serif', letterSpacing: '0.2em', textTransform: 'uppercase', fontSize: '0.75rem' }}>Loading...</div>;

  // If API is unavailable or returns an error, derive display data from the slug
  // so the page template still renders with placeholders instead of a hard error.
  const displayData = (data && !data.error) ? data : (() => {
    const parts = (slug || '').split('-');
    const city = parts[0] ? parts[0].charAt(0).toUpperCase() + parts[0].slice(1) : 'Gallery';
    const type = parts.slice(1).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') || 'Project';
    return { slug, city, type, title: `${city} ${type}`, images: [], count: 0 };
  })();

  const faqs = getFaqs(displayData.type);

  return (
    <>
      <style>{galleryStyles}</style>

      {/* HERO */}
      <div className="gallery-hero">
        {displayData.heroImage
          ? <img src={imgSrc(displayData.heroImage)} srcSet={imgSrcSet(displayData.heroImage)} sizes="100vw" alt={displayData.title} />
          : <div className="hero-placeholder" />}
        <div className="gallery-hero-overlay">
          <div className="gallery-hero-city">{displayData.city}</div>
          <h1 className="gallery-hero-title">{displayData.type}</h1>
          <div className="gallery-hero-meta">Arana Brothers Construction · {displayData.city}</div>
        </div>
      </div>

      {/* INTRO */}
      <div className="gallery-intro">
        <FadeUp>
          <div className="gallery-intro-label section-eyebrow">The Build</div>
          <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.8rem', fontWeight: 300, color: 'var(--dark)' }}>{displayData.title}</h2>
        </FadeUp>
        <FadeUp delay={150}>
          <p style={{ fontSize: '0.95rem', fontWeight: 300, lineHeight: 1.85, color: 'var(--mid)' }}>
            {displayData.description}
          </p>
        </FadeUp>
      </div>

      {/* IMAGE GRID */}
      <div className="gallery-grid-section">
        <FadeUp>
          <p className="grid-count section-eyebrow">Project Images — {displayData.count} Photos</p>
        </FadeUp>
        <FadeUp delay={100}>
          <div className="image-grid">
            {(displayData.images?.slice(1) || []).length > 0
              ? (displayData.images.slice(1)).map((src, i) => (
                <div key={i} className={`grid-img-wrap${i % 4 === 0 ? ' wide' : ''}`} onClick={() => setLightbox(i)}>
                  <img src={imgSrc(src)} srcSet={imgSrcSet(src)} sizes="(max-width: 900px) 100vw, (max-width: 1100px) 50vw, 33vw" alt={`${displayData.title} ${i + 2}`} />
                  <div className="zoom-hint">View</div>
                </div>
              ))
              : Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className={`grid-img-wrap${i % 4 === 0 ? ' wide' : ''}`}>
                  <div className="img-ph">Image {i + 2}</div>
                </div>
              ))
            }
          </div>
        </FadeUp>
      </div>

      {/* LIGHTBOX */}
      {lightbox !== null && gridImages.length > 0 && (
        <div className="lightbox" onClick={() => setLightbox(null)}>
          <img src={imgSrc(gridImages[lightbox])} srcSet={imgSrcSet(gridImages[lightbox])} sizes="100vw" alt="" onClick={e => e.stopPropagation()} />
          <button className="lb-close" onClick={() => setLightbox(null)}>✕</button>
          <button className="lb-nav lb-prev" onClick={e => { e.stopPropagation(); setLightbox(i => (i - 1 + gridImages.length) % gridImages.length); }}>‹</button>
          <button className="lb-nav lb-next" onClick={e => { e.stopPropagation(); setLightbox(i => (i + 1) % gridImages.length); }}>›</button>
          <div className="lb-counter">{lightbox + 1} / {gridImages.length}</div>
        </div>
      )}

      {/* FAQ */}
      <div className="gallery-faq">
        <FadeUp>
          <div className="section-eyebrow">Ideas & Information</div>
          <h2 className="section-title">Common Questions About {displayData.type}</h2>
          <div className="section-rule" />
        </FadeUp>
        <FadeUp delay={100}>
          <div className="faq-list">
            {faqs.map((faq, i) => (
              <div key={i} className={`faq-item${openFaq === i ? ' open' : ''}`}>
                <button className="faq-trigger" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  <span className="faq-q-text">{faq.q}</span>
                  <span className="faq-icon">+</span>
                </button>
                <div className="faq-answer">
                  <p>{faq.a}</p>
                </div>
              </div>
            ))}
          </div>
        </FadeUp>
      </div>

      {/* CTA */}
      <div className="gallery-cta">
        <FadeUp>
          <div>
            <div className="section-eyebrow" style={{ color: 'var(--aqua-light)' }}>Ready to Build</div>
            <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: '2.5rem', fontWeight: 300, color: 'white', lineHeight: 1.2, marginBottom: '0.75rem' }}>Start a Project</h2>
            <p style={{ fontSize: '0.9rem', fontWeight: 300, color: 'rgba(255,255,255,0.55)', lineHeight: 1.75 }}>
              Tell us about your {displayData.type.toLowerCase()} project. We'll follow up within one business day.
            </p>
          </div>
        </FadeUp>
        <FadeUp delay={150}>
          <ContactForm defaultProjectType={displayData.type} />
        </FadeUp>
      </div>
    </>
  );
}

const galleryStyles = `
.gallery-hero { position: relative; height: 72vh; min-height: 480px; overflow: hidden; background: var(--aqua-dark); padding-top: 72px; }
.gallery-hero img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; filter: brightness(0.55); }
.hero-placeholder { position: absolute; inset: 0; background: linear-gradient(135deg, var(--aqua-dark), var(--aqua-light)); opacity: 0.6; }
.gallery-hero-overlay { position: absolute; bottom: 0; left: 0; right: 0; padding: 4rem 5rem 3rem; background: linear-gradient(to top, rgba(26,42,46,0.85), transparent); }
.gallery-hero-city { font-family: 'Barlow Condensed', sans-serif; font-size: 0.7rem; font-weight: 700; letter-spacing: 0.25em; text-transform: uppercase; color: var(--gold); margin-bottom: 0.5rem; }
.gallery-hero-title { font-family: 'Cormorant Garamond', serif; font-size: 3.5rem; font-weight: 300; color: white; line-height: 1.1; margin-bottom: 0.75rem; }
.gallery-hero-meta { font-size: 0.82rem; font-weight: 300; color: rgba(255,255,255,0.5); }
.gallery-intro { padding: 4rem 5rem; display: grid; grid-template-columns: 1fr 2fr; gap: 4rem; border-bottom: 1px solid rgba(141,189,189,0.2); }
.gallery-grid-section { padding: 4rem 5rem; }
.grid-count { margin-bottom: 2rem; }
.image-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 1rem; }
.image-grid .wide { grid-column: span 2; }
.grid-img-wrap { overflow: hidden; background: var(--aqua-dark); cursor: pointer; position: relative; }
.grid-img-wrap img { width: 100%; height: 100%; object-fit: cover; display: block; min-height: 220px; transition: transform 0.45s, filter 0.3s; filter: brightness(0.88); }
.img-ph { min-height: 220px; background: linear-gradient(135deg,rgba(55,100,123,.35),rgba(141,189,189,.35)); display: flex; align-items: center; justify-content: center; font-family:'Barlow Condensed',sans-serif; font-size:.62rem; letter-spacing:.12em; text-transform:uppercase; color:rgba(255,255,255,.3); }
.grid-img-wrap:hover img { transform: scale(1.04); filter: brightness(0.72); }
.zoom-hint { position:absolute; bottom:.75rem; right:.75rem; background:rgba(26,42,46,.7); color:rgba(255,255,255,.7); font-size:.7rem; padding:.3rem .6rem; font-family:'Barlow Condensed',sans-serif; letter-spacing:.1em; opacity:0; transition:opacity .2s; }
.grid-img-wrap:hover .zoom-hint { opacity:1; }
.lightbox { position:fixed; inset:0; background:rgba(26,42,46,.96); z-index:200; display:flex; align-items:center; justify-content:center; }
.lightbox img { max-width:90vw; max-height:88vh; object-fit:contain; }
.lb-close { position:absolute; top:1.5rem; right:2rem; font-size:1.5rem; color:rgba(255,255,255,.5); cursor:pointer; background:none; border:none; transition:color .2s; }
.lb-close:hover { color:white; }
.lb-nav { position:absolute; top:50%; transform:translateY(-50%); background:rgba(255,255,255,.08); border:1px solid rgba(255,255,255,.15); color:rgba(255,255,255,.6); font-size:1.4rem; width:44px; height:44px; display:flex; align-items:center; justify-content:center; cursor:pointer; transition:background .2s; }
.lb-nav:hover { background:rgba(188,134,72,.3); color:white; }
.lb-prev { left:2rem; }
.lb-next { right:2rem; }
.lb-counter { position:absolute; bottom:1.5rem; font-family:'Barlow Condensed',sans-serif; font-size:.7rem; letter-spacing:.2em; color:rgba(255,255,255,.3); }
.gallery-faq { background:var(--off-white); padding:5rem; }
.faq-list { display:flex; flex-direction:column; }
.faq-item { border-bottom:1px solid rgba(141,189,189,.25); }
.faq-trigger { width:100%; background:none; border:none; text-align:left; padding:1.5rem 0; display:flex; justify-content:space-between; align-items:flex-start; cursor:pointer; gap:2rem; }
.faq-q-text { font-family:'Barlow',sans-serif; font-size:.95rem; font-weight:500; color:var(--aqua-dark); line-height:1.4; }
.faq-icon { font-size:1rem; color:var(--gold); flex-shrink:0; transition:transform .3s; margin-top:2px; }
.faq-item.open .faq-icon { transform:rotate(45deg); }
.faq-answer { max-height:0; overflow:hidden; transition:max-height .4s ease, padding .3s; }
.faq-item.open .faq-answer { max-height:300px; padding-bottom:1.5rem; }
.faq-answer p { font-size:.88rem; font-weight:300; line-height:1.8; color:var(--mid); }
.gallery-cta { background:var(--aqua-dark); padding:5rem; display:grid; grid-template-columns:1fr 1fr; gap:4rem; align-items:start; }
@media(max-width:1100px) {
  .gallery-grid-section { padding:3rem 2.5rem; }
  .image-grid { grid-template-columns:repeat(2,1fr); }
  .image-grid .wide { grid-column:span 2; }
  .gallery-intro { padding:3rem 2.5rem; gap:2rem; }
  .gallery-faq, .gallery-cta { padding:4rem 2.5rem; }
}
@media(max-width:900px) {
  .gallery-hero-overlay { padding:3rem 2rem 2rem; }
  .gallery-hero-title { font-size:2.2rem; }
  .gallery-intro { grid-template-columns:1fr; padding:3rem 2rem; gap:1.5rem; }
  .gallery-grid-section { padding:3rem 2rem; }
  .image-grid { grid-template-columns:1fr; }
  .image-grid .wide { grid-column:span 1; }
  .gallery-faq, .gallery-cta { padding:4rem 2rem; }
  .gallery-cta { grid-template-columns:1fr; }
}
`;
